import { resolveUserFacingApiFailureMessage } from "@/store/propertyCore/propertyInactiveMessage";

/**
 * @param {string} value
 * @returns {unknown|null}
 */
function tryParseJsonErrorString(value) {
  const trimmed = value.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
}

/**
 * Coerce unknown thrown/API values into a human-readable error string.
 * Avoids classic `String({})` / `obj.toString()` → "[object Object]".
 *
 * @param {unknown} value
 * @param {string} [fallback]
 * @param {{ forDisplay?: boolean }} [options] When `forDisplay` is true, opaque
 *   objects fall back instead of JSON.stringify (safer for snackbars/inbox).
 * @returns {string}
 */
export function toErrorMessage(value, fallback = "Unexpected error", options = {}) {
  const forDisplay = Boolean(options?.forDisplay);
  if (value == null) return fallback;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed || trimmed === "[object Object]" || trimmed === "[object Error]") {
      return fallback;
    }
    const parsedJson = tryParseJsonErrorString(trimmed);
    if (parsedJson != null) {
      return toErrorMessage(parsedJson, fallback, options);
    }
    return trimmed;
  }
  if (value instanceof Error) {
    return toErrorMessage(value.message, fallback, options);
  }
  if (typeof value === "object") {
    if (typeof value.message === "string") {
      return toErrorMessage(value.message, fallback, options);
    }
    if (typeof value.error === "string") {
      return toErrorMessage(value.error, fallback, options);
    }
    if (value.message != null && typeof value.message === "object") {
      return toErrorMessage(value.message, fallback, options);
    }
    if (value.error != null && typeof value.error === "object") {
      return toErrorMessage(value.error, fallback, options);
    }
    if (Array.isArray(value.errors) && value.errors.length > 0) {
      const parts = value.errors
        .map((entry) => toErrorMessage(entry, "", options))
        .filter(Boolean);
      if (parts.length > 0) return parts.join("; ");
    }
    if (forDisplay) return fallback;
    try {
      const json = JSON.stringify(value);
      if (typeof json === "string" && json.length > 0 && json !== "{}" && json !== "[]") {
        return json;
      }
    } catch {
      // circular / non-serializable
    }
    return fallback;
  }
  const asString = String(value);
  if (!asString || asString === "[object Object]" || asString === "[object Error]") {
    return fallback;
  }
  return asString;
}

/**
 * Display-safe error coercion for UI (toasts / inbox). Never JSON.stringifies
 * opaque objects — returns `fallback` instead.
 *
 * @param {unknown} value
 * @param {string} [fallback]
 * @returns {string}
 */
function shouldApplyApiFailureMapping(value, coerced) {
  if (value == null) return false;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (tryParseJsonErrorString(trimmed) != null) return true;
    if (trimmed.toLowerCase().includes("forbidden")) return true;
  }
  if (typeof value === "object" && !Array.isArray(value)) {
    if ("statusCode" in value || "status" in value || "code" in value || "error" in value) {
      return true;
    }
    if (typeof value.message === "string" && value.message.trim().startsWith("{")) {
      return true;
    }
  }
  return String(coerced ?? "").trim().toLowerCase() === "forbidden";
}

export function toDisplayErrorMessage(value, fallback = "Unexpected error") {
  const coerced = toErrorMessage(value, fallback, { forDisplay: true });
  if (coerced === "[object Object]" || coerced === "[object Error]") {
    return fallback;
  }
  if (!shouldApplyApiFailureMapping(value, coerced)) {
    return coerced;
  }
  return resolveUserFacingApiFailureMessage(value ?? coerced, coerced);
}
